import React, { useState } from "react";
import { format, parse, isValid } from "date-fns";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { CalendarDays, X } from "lucide-react";

const toDate = (s, f) => { if (!s) return undefined; const d = parse(s, f, new Date()); return isValid(d) ? d : undefined; };
const HOURS = Array.from({ length: 24 }, (_, i) => String(i).padStart(2, "0"));
const MINS = ["00", "15", "30", "45"];

function Picker({ value, onSelect, years }) {
  const maxDay = years[1] === new Date().getFullYear() ? new Date() : undefined;
  return <Calendar mode="single" selected={value} defaultMonth={value} onSelect={onSelect} captionLayout="dropdown-buttons" toDate={maxDay}
    fromYear={years[0]} toYear={years[1]} initialFocus
    classNames={{ caption_label: "hidden", caption_dropdowns: "flex gap-1 justify-center", dropdown: "h-8 rounded-md border bg-background px-1 text-sm", vhidden: "sr-only" }} />;
}

export function DatePicker({ value, onChange, placeholder = "Pick a date", className = "", testId, years = [1940, new Date().getFullYear() + 10] }) {
  const [open, setOpen] = useState(false);
  const d = toDate(value, "yyyy-MM-dd");
  return (
    <div className={`relative ${className}`}>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button type="button" variant="outline" className={`w-full justify-start font-normal pr-8 ${!d ? "text-muted-foreground" : ""}`} data-testid={testId}>
            <CalendarDays className="h-4 w-4 mr-2 shrink-0" />{d ? format(d, "dd MMM yyyy") : placeholder}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start">
          <Picker value={d} years={years} onSelect={(x) => { if (x) { onChange(format(x, "yyyy-MM-dd")); setOpen(false); } }} />
        </PopoverContent>
      </Popover>
      {d && <button type="button" aria-label="Clear date" onClick={(e) => { e.preventDefault(); e.stopPropagation(); onChange(""); }} className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded opacity-60 hover:opacity-100" data-testid={testId && `${testId}-clear`}><X className="h-3.5 w-3.5" /></button>}
    </div>
  );
}

export function DateTimePicker({ value, onChange, placeholder = "Pick date & time", className = "", testId }) {
  const [open, setOpen] = useState(false);
  const d = toDate(value, "yyyy-MM-dd'T'HH:mm");
  const hh = d ? format(d, "HH") : "10";
  const mm = d ? String(Math.floor(d.getMinutes() / 15) * 15).padStart(2, "0") : "00";
  const emit = (day, h, m) => onChange(day ? `${format(day, "yyyy-MM-dd")}T${h}:${m}` : "");
  const sel = "h-9 rounded-md border bg-background px-2 text-sm";
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button type="button" variant="outline" className={`justify-start font-normal w-full ${!d ? "text-muted-foreground" : ""} ${className}`} data-testid={testId}>
          <CalendarDays className="h-4 w-4 mr-2 shrink-0" />{d ? format(d, "dd MMM yyyy, hh:mm a") : placeholder}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Picker value={d} years={[new Date().getFullYear() - 1, new Date().getFullYear() + 3]} onSelect={(x) => x && emit(x, hh, mm)} />
        <div className="flex items-center gap-2 border-t p-3">
          <span className="text-xs text-muted-foreground">Time</span>
          <select className={sel} value={hh} onChange={(e) => emit(d || new Date(), e.target.value, mm)} data-testid={testId && `${testId}-hour`}>{HOURS.map((h) => <option key={h} value={h}>{h}</option>)}</select>:
          <select className={sel} value={mm} onChange={(e) => emit(d || new Date(), hh, e.target.value)} data-testid={testId && `${testId}-minute`}>{MINS.map((m) => <option key={m} value={m}>{m}</option>)}</select>
          <Button type="button" size="sm" className="ml-auto" onClick={() => setOpen(false)} data-testid={testId && `${testId}-done`}>Done</Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
